import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { PublicThemeToggle } from '@/components/ui/PublicThemeToggle';
import {
  LEGACY_APP_URL,
  LEGACY_LOGIN_LABEL,
  MEMBER_DOOR_URL,
  MEMBER_SIGN_IN_URL,
  PLATFORM,
  SOLUTIONS,
  TOP_LINKS,
  MOBILE_LINKS,
  type NavGroup,
  type NavLink,
} from './marketingNav';

const TOP =
  'px-2.5 xl:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-ink hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors';
const ITEM =
  'block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-ink';
const ITEM_URGENT =
  'block px-4 py-2.5 text-sm font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-800';
// Emphasis treatment for the Trust.Care login: a left highlight bar in the brand
// teal so the many members who still sign in at trust.care can spot it at a
// glance. Uses the app accent (v2accent) rather than a new colour, so it
// reads the same in light and dark mode.
const ITEM_HIGHLIGHT =
  'block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 border-l-4 border-v2accent bg-v2accent-soft hover:bg-v2accent-soft hover:text-ink';
const CHIP =
  'flex-shrink-0 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 whitespace-nowrap hover:border-v2accent';
const CHIP_URGENT =
  'flex-shrink-0 px-3 py-2 text-sm font-semibold text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-lg border border-red-200 dark:border-red-500/30 whitespace-nowrap hover:border-red-400';

const Dropdown: React.FC<{ group: NavGroup }> = ({ group }) => (
  <div className="relative group">
    <button className={`flex items-center ${TOP}`}>
      {group.label}
      <ChevronDown className="w-4 h-4 ml-1" />
    </button>
    <div
      className={`absolute top-full ${group.alignRight ? 'right-0' : 'left-0'} mt-1 w-64 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}
    >
      <div className="py-2">
        {group.links.map((link: NavLink) => (
          <Link key={link.to} to={link.to} className={link.urgent ? ITEM_URGENT : ITEM}>
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

interface MarketingHeaderProps {
  /** Staff login. Falls back to the /login route when not supplied. */
  onLogin?: () => void;
}

/**
 * The public site's one header. Carries the full navigation, so a visitor who
 * lands on a Solutions page from a search result can reach the rest of the
 * site — which the per-page "Back to Home" headers did not allow.
 */
const MarketingHeader: React.FC<MarketingHeaderProps> = ({ onLogin }) => (
  <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50">
    {/* The member door (TRUS-778): one thin line for people who came here for
        help for themselves, above an organization-facing site. */}
    <div className="bg-v2accent text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex flex-wrap items-center justify-center gap-x-2 text-sm text-center">
        <span>Looking for help for yourself or your family?</span>
        <Link to={MEMBER_DOOR_URL} className="font-semibold underline underline-offset-2 hover:no-underline">
          Get help
        </Link>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between py-2">
        {/* weavein-wordmark.png, not weavin-logo.png: the latter is 80% transparent
            padding (247px of ink on a 1224px canvas), so `h-40` rendered 28px of
            wordmark floating in 112px of nothing. That stranded "Powered by Trust"
            ~45px below the mark, pushed the header to 153px tall, and left the two
            left edges misaligned because the padding is asymmetric horizontally too.
            The cropped asset means the h-* value is the size you actually see, so
            this stacks with an ordinary gap and no negative margins. */}
        <Link to="/" className="flex items-center flex-shrink-0" aria-label="WeaveIn home">
          <img src="/weavein-wordmark.png" alt="WeaveIn" className="h-8 lg:h-10 xl:h-11 w-auto shrink-0" />
        </Link>

        <nav className="hidden lg:flex items-center space-x-1">
          <Link to={PLATFORM.to} className={TOP}>
            {PLATFORM.label}
          </Link>
          <Dropdown group={SOLUTIONS} />
          {TOP_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className={TOP}>
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center space-x-2 md:space-x-3">
          <PublicThemeToggle />
          <Link
            to="/signup"
            className="hidden sm:block bg-ink text-paper px-5 py-2.5 rounded-lg hover:bg-ink-700 transition-colors text-sm font-semibold"
          >
            Sign Up
          </Link>
          <div className="relative group">
            <button className="px-2.5 xl:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-ink whitespace-nowrap">
              Login
              <ChevronDown className="w-4 h-4 ml-0.5 inline" />
            </button>
            <div className="absolute top-full right-0 mt-1 w-60 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="py-2">
                {onLogin ? (
                  <button onClick={onLogin} className={`${ITEM} w-full text-left`}>
                    WeaveIn
                  </button>
                ) : (
                  <Link to="/login" className={ITEM}>
                    WeaveIn
                  </Link>
                )}
                <Link to={MEMBER_SIGN_IN_URL} className={ITEM}>
                  Member sign in
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    For people getting help
                  </span>
                </Link>
                <a href={LEGACY_APP_URL} className={ITEM_HIGHLIGHT}>
                  {LEGACY_LOGIN_LABEL}
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    app.trust.care
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="lg:hidden border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
      <div className="overflow-x-auto scrollbar-hide">
        <nav className="flex px-4 py-2 space-x-1 min-w-max">
          {MOBILE_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className={link.urgent ? CHIP_URGENT : CHIP}>
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  </header>
);

export default MarketingHeader;
